import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal, FadeIn } from "@/components/Reveal";
import { PROJECTS } from "@/data/content";

const ProjectCard = ({ project, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <Reveal delay={(index % 3) * 0.08} className="mb-8 break-inside-avoid">
      <article
        ref={ref}
        data-testid={`portfolio-card-${project.id}`}
        className="group relative overflow-hidden border border-white/8"
      >
        <div className={`${project.ratio} overflow-hidden`}>
          <motion.img
            src={project.image}
            alt={project.name}
            loading="lazy"
            style={{ y }}
            className="img-luxe h-[115%] w-full scale-110 object-cover transition-transform duration-700 group-hover:scale-[1.16]"
          />
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-noir/95 via-noir/20 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
        <div className="absolute inset-x-0 bottom-0 p-7">
          <p className="text-[10px] uppercase tracking-[0.3em] text-champagne">
            {project.category}
          </p>
          <div className="mt-2 flex items-end justify-between gap-4">
            <h2 className="font-serif text-2xl font-light text-ivoire sm:text-3xl">
              {project.name}
            </h2>
            <ArrowUpRight
              size={20}
              strokeWidth={1}
              className="mb-1 shrink-0 text-champagne opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-x-0 group-hover:opacity-100 -translate-x-2"
            />
          </div>
          <p className="mt-3 max-h-0 overflow-hidden text-sm leading-relaxed text-ivoire/65 opacity-0 transition-[max-height,opacity] duration-500 group-hover:max-h-24 group-hover:opacity-100">
            {project.description}
          </p>
        </div>
      </article>
    </Reveal>
  );
};

const Portfolio = () => (
  <div data-testid="portfolio-page">
    {/* ————— HERO ————— */}
    <section className="px-6 pb-20 pt-44 sm:px-10 sm:pb-28 sm:pt-56">
      <div className="mx-auto max-w-6xl">
        <FadeIn delay={0.2}>
          <p className="overline-label">Portfolio</p>
        </FadeIn>
        <h1 className="mt-10 font-serif font-light leading-[1.05] tracking-tight text-ivoire">
          <MaskedLine delay={0.4} className="text-4xl sm:text-6xl lg:text-7xl">
            Des univers graphiques
          </MaskedLine>
          <MaskedLine delay={0.58} className="text-4xl sm:text-6xl lg:text-7xl">
            <em className="italic text-champagne">conçus pour durer.</em>
          </MaskedLine>
        </h1>
        <FadeIn delay={0.9}>
          <p className="mt-10 max-w-2xl text-base leading-relaxed text-ivoire/65 sm:text-lg">
            Une sélection de projets d'étude — identités, éditions, packagings et
            sites imaginés avec l'exigence que nous apportons à chaque client.
          </p>
        </FadeIn>
      </div>
    </section>

    {/* ————— GALERIE ————— */}
    <section className="mx-auto max-w-7xl px-6 pb-28 sm:px-10 sm:pb-40">
      <div className="columns-1 gap-8 sm:columns-2 lg:columns-3">
        {PROJECTS.map((p, i) => (
          <ProjectCard key={p.id} project={p} index={i} />
        ))}
      </div>
    </section>

    {/* ————— CTA ————— */}
    <section className="border-t border-white/5 py-28 sm:py-36">
      <div className="mx-auto max-w-3xl px-6 text-center sm:px-10">
        <Reveal>
          <p className="overline-label">Et si le prochain était le vôtre ?</p>
          <h2 className="mt-8 font-serif text-3xl font-light leading-tight tracking-tight text-ivoire sm:text-5xl">
            Écrivons la suite de <em className="italic text-champagne">votre histoire.</em>
          </h2>
          <Link
            to="/contact"
            data-testid="portfolio-final-cta"
            className="group mt-12 inline-flex items-center gap-3 rounded-full bg-champagne px-10 py-4 text-[13px] uppercase tracking-[0.2em] text-noir transition-[background-color] duration-400 hover:bg-ivoire"
          >
            Démarrer un projet
            <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-400 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Portfolio;
